<template>
  <div>
    <v-row>
      <v-col cols="12" sm="6">
        <v-text-field
            :label="nameLabel"
            class="required unique"
            :model-value="name"
            :error-messages="errorMessages.name"
            @update:model-value="$emit('update:name', $event); validateName($event)"
            @blur="validateName()"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <ontos-computed-input
            :label="$tt('alias')"
            class="required unique ontos-code"
            :value="alias"
            @input="$emit('update:alias', $event); validateAlias($event)"
            @blur="validateAlias()"
            :error-messages="errorMessages.alias"
            :source="name"
            :manual="manualAlias"
            @input:manual="manualAlias = $event"
            :compute="$utils.aliasSuggestion"
            :computeOnInput="$utils.incompleteAliasSuggestion"
            :readonly="fixedAlias"
        />
      </v-col>
    </v-row>

    <!-- A lista, de quem usa: as opções do domínio, as situações do fluxo -->
    <slot/>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ComputedInput from "@/components/shared/ComputedInput.vue";

/**
 * A identidade de um objeto do ambiente que é uma lista — o domínio e o fluxo de trabalho: nome e
 * alias, únicos entre os irmãos. A lista vem pelo slot, e quem a valida é quem a trouxe.
 *
 * A identidade continua num componente, e não solta em cada editor, porque o `manualAlias` não é um
 * campo mas uma máquina de três peças (`startWithAutoAlias` → `created` → o `manual` do
 * ComputedInput), que é onde já houve defeito.
 */
@Component({
  name: 'ontos-named-list-input',
  emits: ['update:name', 'update:alias'],
  components: {'ontos-computed-input': ComputedInput}
})
class NamedListInput extends Vue {
  @Prop({type: String, required: true}) readonly name!: string
  @Prop({type: String, required: true}) readonly alias!: string
  @Prop({type: String, required: true}) readonly nameLabel!: string
  @Prop({type: Boolean, default: false}) readonly fixedAlias!: boolean
  @Prop({type: Boolean, default: false}) readonly startWithAutoAlias!: boolean
  /** Os irmãos do ambiente, para a unicidade do próprio objeto. Só nome e alias são lidos. */
  @Prop({type: Array, default: () => []}) readonly siblings!: Array<{ name: string, alias: string }>

  manualAlias = true
  errorMessages: { name: string, alias: string } = {name: '', alias: ''}

  /**
   * No created, e não no inicializador do campo: ali as props ainda não existem, e
   * `!undefined` daria sempre `true` — o alias deixaria de ser sugerido automaticamente.
   */
  created() {
    this.manualAlias = !this.startWithAutoAlias
  }

  validateName(value?: string): boolean {
    return this.validateSelf('name', value === undefined ? this.name : value)
  }

  validateAlias(value?: string): boolean {
    return this.validateSelf('alias', value === undefined ? this.alias : value)
  }

  /** Nível objeto: nome e alias exigidos e únicos entre os IRMÃOS do ambiente. */
  private validateSelf(field: 'name' | 'alias', value: string): boolean {
    if (!String(value ?? '').trim()) {
      this.errorMessages[field] = this.$tt('required-message') as string
      return false
    }
    //Na edição o alias é fixo, e o próprio objeto está na lista: ele não conta. Na criação
    //não há "próprio" — excluir pelo alias digitado faria justamente o alias repetido nunca
    //ser acusado.
    const self = this.fixedAlias ? this.alias : undefined
    const taken = this.siblings.some(sibling => sibling.alias !== self && sibling[field] === value)
    this.errorMessages[field] = taken ? this.$tt('validation.already-used', {value}) as string : ''
    return !taken
  }

  /** Sem curto-circuito: marca os dois campos de uma vez. Quem hospeda chama pelo `$refs`. */
  validateModel(): boolean {
    const isValid = this.validateName()
    return this.validateAlias() && isValid
  }
}

export default toNative(NamedListInput)
</script>
